import * as Tone from 'tone';

export default class SoundManager {
    constructor() {
        this.initialized = false;
        this.soundEffects = new Map();
        this.loadedSounds = new Set();
        this.duckSoundIndex = 0; // Track which duck sound to play
        this.init();
    }

    async init() {
        try {
            await this.setupSounds();
            this.initialized = true;
        } catch (error) {
            console.error('Failed to initialize sound system:', error);
        }
    }

    async setupSounds() {
        try {
            // Load audio files with correct paths
            const sounds = {
                keyPress: new Tone.Player({
                    url: '/sounds/mixkit-single-key-type-2533.wav',
                    onload: () => this.loadedSounds.add('keyPress')
                }).toDestination(),
                keyTypeHard: new Tone.Player({
                    url: '/sounds/mixkit-hard-laptop-keyboard-typing-2538.wav',
                    onload: () => this.loadedSounds.add('keyTypeHard')
                }).toDestination(),
                mouseClick: new Tone.Player({
                    url: '/sounds/mixkit-fast-double-click-on-mouse-275.wav',
                    onload: () => this.loadedSounds.add('mouseClick')
                }).toDestination(),
                paperTrash: new Tone.Player({
                    url: '/sounds/mixkit-pile-of-paper-trash-2381.wav',
                    onload: () => this.loadedSounds.add('paperTrash')
                }).toDestination(),
                glitch: new Tone.Player({
                    url: '/sounds/mixkit-small-electric-glitch-2595.wav',
                    onload: () => this.loadedSounds.add('glitch')
                }).toDestination(),
                duckSqueak: new Tone.Player({
                    url: '/sounds/duck/mixkit-rubber-duck-squeak-1014.wav',
                    onload: () => this.loadedSounds.add('duckSqueak')
                }).toDestination(),
                duckSqueeze: new Tone.Player({
                    url: '/sounds/duck/mixkit-bath-duck-squeeze-1882.wav',
                    onload: () => this.loadedSounds.add('duckSqueeze')
                }).toDestination(),
                plantRustle: new Tone.Player({
                    url: '/sounds/mixkit-dry-leaves-sound-2428.wav',
                    onload: () => this.loadedSounds.add('plantRustle')
                }).toDestination()
            };

            // Set volume and store sounds
            Object.entries(sounds).forEach(([name, player]) => {
                player.volume.value = -15;
                this.soundEffects.set(name, player);
            });

            // Wait for Tone.js to be ready
            await Tone.loaded();
        } catch (error) {
            console.error('Error setting up sounds:', error);
        }
    }

    isSoundLoaded(name) {
        return this.loadedSounds.has(name);
    }

    playSound(name) {
        if (!this.initialized) return;
        
        try {
            switch (name) {
                case 'keyPress':
                    const keyboardSounds = ['keyPress', 'keyTypeHard'];
                    const loadedKeyboardSounds = keyboardSounds.filter(sound => this.isSoundLoaded(sound));
                    if (loadedKeyboardSounds.length > 0) {
                        const randomSound = loadedKeyboardSounds[Math.floor(Math.random() * loadedKeyboardSounds.length)];
                        const player = this.soundEffects.get(randomSound);
                        player?.start();
                    }
                    break;
                case 'mouseClick':
                    if (this.isSoundLoaded('mouseClick')) {
                        const mousePlayer = this.soundEffects.get('mouseClick');
                        mousePlayer?.start();
                    }
                    break;
                case 'paperTrash':
                    if (this.isSoundLoaded('paperTrash')) {
                        const paperPlayer = this.soundEffects.get('paperTrash');
                        paperPlayer?.start();
                    }
                    break;
                case 'duck':
                    const duckSounds = ['duckSqueak', 'duckSqueeze'];
                    const soundToPlay = duckSounds[this.duckSoundIndex];
                    if (this.isSoundLoaded(soundToPlay)) {
                        const duckPlayer = this.soundEffects.get(soundToPlay);
                        duckPlayer?.start();
                        // Toggle between 0 and 1 for next time
                        this.duckSoundIndex = (this.duckSoundIndex + 1) % 2;
                    }
                    break;
                case 'plant':
                    if (this.isSoundLoaded('plantRustle')) {
                        const plantPlayer = this.soundEffects.get('plantRustle');
                        plantPlayer?.start();
                    }
                    break;
                case 'screenChange':
                    if (Math.random() < 0.3 && this.isSoundLoaded('glitch')) {
                        const glitchPlayer = this.soundEffects.get('glitch');
                        glitchPlayer?.start();
                    }
                    break;
            }
        } catch (error) {
            console.error(`Error playing sound ${name}:`, error);
        }
    }

    setVolume(name, volume) {
        if (!this.initialized) return;
        try {
            const sound = this.soundEffects.get(name);
            if (sound) {
                sound.volume.value = volume - 15;
            }
        } catch (error) {
            console.error(`Error setting volume for ${name}:`, error);
        }
    }

    stopAllSounds() {
        if (!this.initialized) return;
        try {
            this.soundEffects.forEach(sound => sound?.stop());
            Tone.Transport.stop();
        } catch (error) {
            console.error('Error stopping sounds:', error);
        }
    }
} 